import React, { useRef, useState } from 'react';
import { message } from 'antd';
import { FieldLabel, TWButton, TWInput, TWModal, TWSelect } from '../../../components/ui';
import fleetApi from '../../../services/fleetApi';
import { putPresigned } from '../../../services/presignedUpload';
import { sha256Hex } from '../../../utils/sha256';
import { fleetErrorText } from '../use-fleet-poll';
import { APPS, formatBytes } from '../fleet-utils';

const APP_OPTIONS = APPS.map(({ value, label }) => ({ value, label }));
const FILE_INPUT_ID = 'release-upload-file';
const MAX_VERSION_CODE = 2147483647;

// Whole number, 1 or more, within an Int32 (the API column).
const parseVersionCode = (text) => {
  const trimmed = String(text).trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= 1 && n <= MAX_VERSION_CODE ? n : null;
};

const isApk = (file) => /\.apk$/i.test(file?.name || '');

// Hash in the browser, presign, PUT straight to storage, then confirm (P-4).
// The sequence is strictly ordered: nothing after a failing step runs, and a
// retry runs the whole sequence again.
const ReleaseUploadModal = ({ open, onClose, onUploaded }) => {
  const [app, setApp] = useState(undefined);
  const [versionName, setVersionName] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [pct, setPct] = useState(0);
  const [error, setError] = useState('');
  const busyRef = useRef(false);

  const code = parseVersionCode(versionCode);
  const codeInvalid = versionCode.trim() !== '' && code === null;
  const canSubmit = Boolean(app && versionName.trim() && code !== null && file) && !busy;

  const reset = () => {
    setApp(undefined);
    setVersionName('');
    setVersionCode('');
    setNotes('');
    setFile(null);
    setFileError('');
    setFileKey((n) => n + 1);
    setPhase('idle');
    setPct(0);
    setError('');
  };

  const close = () => {
    if (busyRef.current) return;
    reset();
    onClose();
  };

  const pickFile = (event) => {
    const picked = event.target.files?.[0] || null;
    if (picked && !isApk(picked)) {
      setFile(null);
      setFileError('Choose an .apk file');
      return;
    }
    setFileError('');
    setFile(picked);
  };

  const submit = async () => {
    if (!canSubmit || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    setPct(0);
    setPhase('hashing');
    let step = 'hash';
    try {
      const sha256 = await sha256Hex(file);
      setPhase('uploading');
      step = 'presign';
      const presign = await fleetApi.presignRelease({
        app,
        versioncode: code,
        sizebytes: file.size,
      });
      const { uploadurl, storagekey, headers } = presign.data.data;
      step = 'put';
      await putPresigned({
        url: uploadurl,
        file,
        headers,
        onProgress: (ratio) => setPct(Math.min(100, Math.round(ratio * 100))),
      });
      step = 'create';
      const created = await fleetApi.createRelease({
        app,
        versionname: versionName.trim(),
        versioncode: code,
        notes: notes.trim(),
        storagekey,
        sha256,
        sizebytes: file.size,
      });
      message.success('Release uploaded');
      onUploaded?.(created.data.data);
      busyRef.current = false;
      setBusy(false);
      reset();
      onClose();
    } catch (err) {
      // putPresigned rejects with a readable "Upload failed (<status>)"; every
      // other step speaks through the API's own string or the fallback.
      const text =
        step === 'put' && !err?.response && err?.message
          ? err.message
          : fleetErrorText(err, 'Upload failed');
      message.error(text);
      setError(text);
      setPct(0);
      setPhase('idle');
      busyRef.current = false;
      setBusy(false);
    }
  };

  const showProgress = busy || Boolean(error);

  return (
    <TWModal
      open={open}
      onClose={close}
      dismissible={!busy}
      size="lg"
      title="Upload release"
      description="The file goes straight to storage; nothing is released until the upload finishes."
      footer={
        <div className="flex flex-row-reverse gap-2">
          <TWButton variant="primary" loading={busy} disabled={!canSubmit} onClick={submit}>
            Upload
          </TWButton>
          <TWButton variant="default" disabled={busy} onClick={close}>
            Cancel
          </TWButton>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <TWSelect
            label="App"
            placeholder="Choose an app"
            options={APP_OPTIONS}
            value={app}
            allowClear={false}
            disabled={busy}
            onChange={setApp}
          />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <TWInput
            label="Version name"
            placeholder="e.g. 3.0.8"
            value={versionName}
            disabled={busy}
            onChange={(e) => setVersionName(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <TWInput
            label="Version code"
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            hint="Whole number, higher than the last build"
            error={codeInvalid ? 'Enter a whole number of 1 or more' : undefined}
            value={versionCode}
            disabled={busy}
            onChange={(e) => setVersionCode(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <TWInput
            label="Notes"
            value={notes}
            disabled={busy}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <FieldLabel htmlFor={FILE_INPUT_ID}>APK file</FieldLabel>
          <div className="rounded-md border border-dashed border-slate-300 px-3 py-3 dark:border-slate-600">
            <input
              key={fileKey}
              id={FILE_INPUT_ID}
              type="file"
              accept=".apk"
              disabled={busy}
              onChange={pickFile}
              className="block w-full text-sm text-slate-600 dark:text-slate-300"
            />
            {file ? (
              <p className="mt-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                {`${file.name} · ${formatBytes(file.size)}`}
              </p>
            ) : null}
          </div>
          {fileError ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{fileError}</p>
          ) : null}
        </div>
        {showProgress ? (
          <div className="col-span-2 space-y-1">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {phase === 'hashing' ? 'Checking file' : `Uploading ${pct}%`}
            </p>
            <div
              role="progressbar"
              aria-label="Upload progress"
              aria-valuenow={pct}
              aria-valuemin="0"
              aria-valuemax="100"
              className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
            >
              <div
                className="h-full rounded-full bg-blue-600 transition-all dark:bg-sky-400"
                style={{ width: `${pct}%` }}
              />
            </div>
            {error ? (
              <p role="alert" className="text-xs text-rose-600 dark:text-rose-400">
                {error}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </TWModal>
  );
};

export default ReleaseUploadModal;
